import moment from "moment";
import CardHESP from "../CardHESP/CardHESP";
import CardHESPTitle from "../CardHESP/CardHESPTitle";
import { Grid } from "@mui/material";
import OrganizationCommitmentUsageSettings from "./OrganizationCommitmentUsageSettings";
import * as React from "react";
import gql from "graphql-tag";
import { useLazyQuery } from "react-apollo";
import {
    GetCommitmentUsageTranscoded,
    GetCommitmentUsageTranscodedVariables,
    GetCommitmentUsageTranscoded_getCommitmentUsageReportTranscoded,
} from "./__generated__/GetCommitmentUsageTranscoded";
import {
    GetCommitmentUsageViewed,
    GetCommitmentUsageViewedVariables,
    GetCommitmentUsageViewed_getCommitmentUsageReportViewed,
} from "./__generated__/GetCommitmentUsageViewed";
import OrganizationCommitmentUsageData, { CommitmentUsageTab } from "./OrganizationCommitmentUsageData";
import { GetFullOrganizationDetails_organization } from "../../views/App/__generated__/GetFullOrganizationDetails";
interface Props {
    organization: GetFullOrganizationDetails_organization;
}

export const GET_COMMITMENT_USAGE_TRANSCODED = gql`
    query GetCommitmentUsageTranscoded($organizationId: ID!, $startDate: String!, $endDate: String!) {
        getCommitmentUsageReportTranscoded(organizationId: $organizationId, startDate: $startDate, endDate: $endDate) {
            items {
                channel {
                    channelId
                    name
                }
                scheduler {
                    schedulerId
                    name
                    clientId
                }
                config {
                    abr
                    fps
                    resolution
                    bitrate
                    drm
                }
                start
                end
                seconds
            }
        }
    }
`;

export const GET_COMMITMENT_USAGE_VIEWED = gql`
    query GetCommitmentUsageViewed($organizationId: ID!, $startDate: String!, $endDate: String!) {
        getCommitmentUsageReportViewed(organizationId: $organizationId, startDate: $startDate, endDate: $endDate) {
            items {
                alias {
                    aliasId
                    name
                }
                channel {
                    channelId
                    name
                }
                scheduler {
                    schedulerId
                    name
                    clientId
                    start
                    end
                }
                config {
                    abr
                    fps
                    resolution
                    bitrate
                    drm
                }
                zone
                src
                hour
                seconds
                gbConsumed
            }
        }
    }
`;

export default function OrganizationCommitmentUsage({ organization }: Props) {
    const [startDate, setStartDate] = React.useState(moment().startOf("month"));
    const [endDate, setEndDate] = React.useState(moment().endOf("day"));
    const [selectedTab, setSelectedTab] = React.useState<CommitmentUsageTab>("transcoded");
    const [hasSearched, setHasSearched] = React.useState(false);

    const [transcodedData, setTranscodedData] = React.useState<
        GetCommitmentUsageTranscoded_getCommitmentUsageReportTranscoded | undefined
    >(undefined);
    const [viewedData, setViewedData] = React.useState<
        GetCommitmentUsageViewed_getCommitmentUsageReportViewed | undefined
    >(undefined);

    const getQueryVariables = () => ({
        endDate: endDate.toISOString(),
        organizationId: organization.organizationId,
        startDate: startDate.toISOString(),
    });

    const [getTranscoded, { loading: loadingTranscoded }] = useLazyQuery<
        GetCommitmentUsageTranscoded,
        GetCommitmentUsageTranscodedVariables
    >(GET_COMMITMENT_USAGE_TRANSCODED, {
        onCompleted: (data) => {
            setTranscodedData(data.getCommitmentUsageReportTranscoded);
        },
        variables: getQueryVariables(),
    });

    const [getViewed, { loading: loadingViewed }] = useLazyQuery<
        GetCommitmentUsageViewed,
        GetCommitmentUsageViewedVariables
    >(GET_COMMITMENT_USAGE_VIEWED, {
        onCompleted: (data) => {
            setViewedData(data.getCommitmentUsageReportViewed);
        },
        variables: getQueryVariables(),
    });

    const handleSearch = () => {
        const variables = getQueryVariables();
        setTranscodedData(undefined);
        setViewedData(undefined);
        setHasSearched(true);

        if (selectedTab === "transcoded") {
            getTranscoded({ variables });
        } else {
            getViewed({ variables });
        }
    };

    const handleTabChange = (tab: CommitmentUsageTab) => {
        setSelectedTab(tab);
        if (tab === "transcoded" && !transcodedData && !loadingTranscoded) {
            getTranscoded({ variables: getQueryVariables() });
        } else if (tab === "viewed" && !viewedData && !loadingViewed) {
            getViewed({ variables: getQueryVariables() });
        }
    };

    const contract =
        organization.contracts.filter(
            (contract) => moment(contract.start) < moment() && moment(contract.end) > moment(),
        )[0] ??
        organization.contracts[0] ??
        undefined;

    const includeBillingInGb = contract?.billInGb === true;

    return (
        <CardHESP>
            <CardHESPTitle title="Usage" subtitle="Calculate seconds transcoded and viewed over a period of time" />
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <OrganizationCommitmentUsageSettings
                        endDate={endDate}
                        onChangeEnd={(val: moment.Moment) => setEndDate(val)}
                        onChangeStart={(val: moment.Moment) => setStartDate(val)}
                        onSubmit={handleSearch}
                        startDate={startDate}
                    />
                </Grid>
                <Grid item xs={12}>
                    <OrganizationCommitmentUsageData
                        selectedTab={selectedTab}
                        onSelectTab={handleTabChange}
                        loadingTranscoded={loadingTranscoded}
                        loadingViewed={loadingViewed}
                        transcodedData={transcodedData}
                        viewedData={viewedData}
                        includeBillingInGb={includeBillingInGb}
                        hasSearched={hasSearched}
                    />
                </Grid>
            </Grid>
        </CardHESP>
    );
}
